import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import '@ingka/button/style.scss';
import '@ingka/focus/style.scss';
import '@ingka/svg-icon/style.scss';
import Button from '@ingka/button';
import play from '@ingka/ssr-icon/paths/play';
import pause from '@ingka/ssr-icon/paths/pause';
import './ReadAloudControls.scss';

const { MARKET_NAME } = process.env.REACT_APP_MARKET_VALUES;

const HIGHLIGHT_CLASS = 'read-aloud-highlight';

const getVoiceForLanguage = (language, availableVoices) => {
    let voices = availableVoices;
    if (!voices || !voices.length) {
        voices =
            typeof window !== 'undefined' && window.speechSynthesis
                ? window.speechSynthesis.getVoices()
                : [];
    }
    if (!voices || !voices.length) {
        return null;
    }

    const normalizedLanguage = (language || '').toLowerCase();
    const languagePrefix = normalizedLanguage.split('-')[0];

    return (
        voices.find((voice) => voice.lang && voice.lang.toLowerCase() === normalizedLanguage) ||
        voices.find(
            (voice) => voice.lang && voice.lang.toLowerCase().startsWith(`${languagePrefix}-`),
        ) ||
        voices.find((voice) => voice.lang && voice.lang.toLowerCase() === languagePrefix) ||
        null
    );
};

const shouldSkipNode = (node) => {
    const parent = node.parentElement;
    if (!parent) {
        return true;
    }

    if (parent.closest('.read-aloud-button')) {
        return true;
    }

    const { tagName } = parent;
    return ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(tagName);
};

const findWordBounds = (text, index) => {
    if (!text) {
        return { start: 0, end: 0 };
    }

    let safeIndex = Math.max(0, Math.min(index, text.length - 1));

    while (safeIndex < text.length && /\s/.test(text[safeIndex])) {
        safeIndex += 1;
    }

    if (safeIndex >= text.length) {
        return { start: text.length, end: text.length };
    }

    let start = safeIndex;
    while (start > 0 && !/\s/.test(text[start - 1])) {
        start -= 1;
    }

    let end = safeIndex;
    while (end < text.length && !/\s/.test(text[end])) {
        end += 1;
    }

    return { start, end };
};

const BLOCK_TAGS = new Set([
    'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DETAILS', 'DIV', 'DL', 'DT',
    'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5',
    'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'TABLE',
    'TR', 'UL',
]);

// A short pause inserted between block elements so the speech doesn't run
// straight from the end of one block (heading, paragraph, list item...) into
// the next one.
const BLOCK_SEPARATOR = '. \n';

const blockLevelCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

const isBlockLevelElement = (element) => {
    // Fast path for well-known block tags (avoids a layout read).
    if (BLOCK_TAGS.has(element.tagName)) {
        return true;
    }
    if (blockLevelCache && blockLevelCache.has(element)) {
        return blockLevelCache.get(element);
    }
    let result = false;
    if (typeof window !== 'undefined' && window.getComputedStyle) {
        const { display } = window.getComputedStyle(element);
        result =
            display === 'block' ||
            display === 'flex' ||
            display === 'grid' ||
            display === 'list-item' ||
            display.startsWith('table');
    }
    if (blockLevelCache) {
        blockLevelCache.set(element, result);
    }
    return result;
};

const getBlockAncestor = (node) => {
    let element = node.parentElement;
    while (element) {
        if (isBlockLevelElement(element)) {
            return element;
        }
        element = element.parentElement;
    }
    return null;
};

const endsWithSentencePunctuation = (text) => /[.!?:;]["')\]]?\s*$/.test(text);

const createReadableContent = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            if (shouldSkipNode(node)) {
                return NodeFilter.FILTER_REJECT;
            }

            if (!node.nodeValue || !node.nodeValue.trim()) {
                return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
        },
    });

    const segments = [];
    const chunks = [];
    let cursor = 0;
    let previousBlock = null;
    let previousChunk = '';
    let current = walker.nextNode();

    while (current) {
        const block = getBlockAncestor(current);

        // When we cross into a different block element, add a pause unless the
        // previous block already ended with sentence punctuation.
        if (
            previousBlock &&
            block !== previousBlock &&
            previousChunk.trim() &&
            !endsWithSentencePunctuation(previousChunk)
        ) {
            chunks.push(BLOCK_SEPARATOR);
            cursor += BLOCK_SEPARATOR.length;
        }

        const chunk = current.nodeValue;
        segments.push({
            node: current,
            start: cursor,
            end: cursor + chunk.length,
            text: chunk,
        });
        chunks.push(chunk);
        cursor += chunk.length;

        previousBlock = block;
        previousChunk = chunk;
        current = walker.nextNode();
    }

    return {
        text: chunks.join(''),
        segments,
    };
};

const ReadAloudControls = () => {
    const location = useLocation();
    const [isPaused, setIsPaused] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const utteranceRef = useRef(null);
    const readingMapRef = useRef({ text: '', segments: [] });
    const highlightRef = useRef(null);

    const isDenmarkMarket = MARKET_NAME === 'dk-da';
    const [floatingContainer, setFloatingContainer] = useState(() =>
        typeof document !== 'undefined' ? document.querySelector('#hnf-floating-container') : null,
    );
    const voicesRef = useRef([]);

    const getRootElement = useCallback(() => {
        return document.querySelector('.page-container__main') || document.body;
    }, []);

    const clearHighlight = useCallback(() => {
        const activeHighlight = highlightRef.current;
        if (!activeHighlight) {
            return;
        }

        const { parent, beforeNode, markNode, afterNode } = activeHighlight;
        if (!parent || !beforeNode || !markNode || !afterNode) {
            highlightRef.current = null;
            return;
        }

        const restoredNode = document.createTextNode(
            `${beforeNode.nodeValue}${markNode.textContent}${afterNode.nodeValue}`,
        );

        if (parent.contains(beforeNode)) {
            parent.replaceChild(restoredNode, beforeNode);
        }

        if (parent.contains(markNode)) {
            parent.removeChild(markNode);
        }

        if (parent.contains(afterNode)) {
            parent.removeChild(afterNode);
        }

        highlightRef.current = null;
    }, []);

    const getReadableText = useCallback(() => {
        const root = getRootElement();
        if (!root) {
            return '';
        }
        const readableContent = createReadableContent(root);
        readingMapRef.current = readableContent;

        return readableContent.text;
    }, [getRootElement]);

    const highlightAtCharIndex = useCallback(
        (charIndex) => {
            clearHighlight();

            const root = getRootElement();
            const readableContent = createReadableContent(root);
            readingMapRef.current = readableContent;

            const { text, segments } = readableContent;
            if (!text || !segments.length) {
                return;
            }

            const safeIndex = Math.max(0, Math.min(charIndex, text.length - 1));
            const segment = segments.find(
                ({ start, end }) => safeIndex >= start && safeIndex < end,
            );
            if (!segment || !segment.node || !segment.node.parentNode) {
                return;
            }

            const localIndex = safeIndex - segment.start;
            const { start, end } = findWordBounds(segment.text, localIndex);
            if (start === end) {
                return;
            }

            const parent = segment.node.parentNode;
            const beforeNode = document.createTextNode(segment.text.slice(0, start));
            const markNode = document.createElement('mark');
            markNode.className = HIGHLIGHT_CLASS;
            markNode.textContent = segment.text.slice(start, end);
            const afterNode = document.createTextNode(segment.text.slice(end));

            const fragment = document.createDocumentFragment();
            fragment.appendChild(beforeNode);
            fragment.appendChild(markNode);
            fragment.appendChild(afterNode);

            parent.replaceChild(fragment, segment.node);
            highlightRef.current = { parent, beforeNode, markNode, afterNode };
        },
        [clearHighlight, getRootElement],
    );

    const cleanupSpeech = useCallback(() => {
        if (!window.speechSynthesis) {
            return;
        }
        window.speechSynthesis.cancel();
        // Chrome can leave the engine stuck in a paused state after cancel();
        // resuming clears that flag so the next page can start reading again.
        if (window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
        }
        clearHighlight();
        utteranceRef.current = null;
        setIsPaused(false);
        setIsSpeaking(false);
    }, [clearHighlight]);

    const startReading = useCallback(() => {
        if (!window.speechSynthesis) {
            return;
        }

        const text = getReadableText();
        if (!text) {
            return;
        }

        window.speechSynthesis.cancel();
        // Clear any stuck paused state before starting a new utterance.
        window.speechSynthesis.resume();
        clearHighlight();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'da-DK';
        utterance.rate = 1;
        utterance.pitch = 1;

        const voices = voicesRef.current.length
            ? voicesRef.current
            : window.speechSynthesis.getVoices();
        const selectedVoice = getVoiceForLanguage('da-DK', voices);
        if (selectedVoice) {
            utterance.voice = selectedVoice;
            utterance.lang = selectedVoice.lang || 'da-DK';
        }

        utterance.onstart = () => {
            setIsSpeaking(true);
            setIsPaused(false);
        };

        utterance.onend = () => {
            setIsSpeaking(false);
            setIsPaused(false);
            clearHighlight();
            utteranceRef.current = null;
        };

        utterance.onerror = () => {
            setIsSpeaking(false);
            setIsPaused(false);
            clearHighlight();
            utteranceRef.current = null;
        };

        utterance.onboundary = (event) => {
            if (typeof event.charIndex !== 'number') {
                return;
            }
            highlightAtCharIndex(event.charIndex);
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    }, [clearHighlight, getReadableText, highlightAtCharIndex]);

    const pauseReading = useCallback(() => {
        if (!window.speechSynthesis) {
            return;
        }
        window.speechSynthesis.pause();
        setIsPaused(true);
    }, []);

    const resumeReading = useCallback(() => {
        if (!window.speechSynthesis) {
            return;
        }
        window.speechSynthesis.resume();
        setIsPaused(false);
        setIsSpeaking(true);
    }, []);

    const toggleReading = useCallback(() => {
        if (!window.speechSynthesis) {
            return;
        }

        if (isSpeaking && !isPaused) {
            pauseReading();
            return;
        }

        if (isSpeaking && isPaused) {
            resumeReading();
            return;
        }

        startReading();
    }, [isSpeaking, isPaused, pauseReading, resumeReading, startReading]);

    useEffect(() => {
        if (!isDenmarkMarket || typeof window === 'undefined' || !('speechSynthesis' in window)) {
            return undefined;
        }

        cleanupSpeech();

        const loadVoices = () => {
            voicesRef.current = window.speechSynthesis.getVoices() || [];
        };
        loadVoices();
        window.speechSynthesis.addEventListener('voiceschanged', loadVoices);

        return () => {
            window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
            cleanupSpeech();
        };
    }, [cleanupSpeech, isDenmarkMarket]);

    useEffect(() => {
        if (!isDenmarkMarket || typeof document === 'undefined' || floatingContainer) {
            return undefined;
        }

        const resolveContainer = () => document.querySelector('#hnf-floating-container');

        const existing = resolveContainer();
        if (existing) {
            setFloatingContainer(existing);
            return undefined;
        }

        const observer = new MutationObserver(() => {
            const found = resolveContainer();
            if (found) {
                setFloatingContainer(found);
                observer.disconnect();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [isDenmarkMarket, floatingContainer]);

    useEffect(() => {
        if (isDenmarkMarket) {
            cleanupSpeech();
        }
    }, [location.pathname, cleanupSpeech, isDenmarkMarket]);

    if (
        !isDenmarkMarket ||
        typeof window === 'undefined' ||
        !('speechSynthesis' in window) ||
        !floatingContainer
    ) {
        return null;
    }

    return ReactDOM.createPortal(
        <Button
            type="secondary"
            iconOnly
            className="read-aloud-button"
            aria-label={isSpeaking && !isPaused ? 'Sæt oplæsning på pause' : 'Afspil oplæsning'}
            ssrIcon={isSpeaking && !isPaused ? pause : play}
            onClick={toggleReading}
        />,
        floatingContainer,
    );
};

export default ReadAloudControls;
